<script lang="ts">
import { RouteNames } from "@/utils/constants.ts";

export function getRouteXAccountsCryptoExchange() {
  return {
    name: RouteNames.X_EXCHANGE_CRYPTO,
  };
}
</script>
<script setup lang="ts">
import BreadCrumbs from "@/components/BreadCrumbs.vue";
import BasicCard from "@/components/BasicCard.vue";
import TextInput from "@/components/form/TextInput.vue";
import InputSelect from "@/components/form/InputSelect.vue";
import { ref, computed, watch } from "vue";
import { 
  ArrowRight as ArrowRightIcon,
  ArrowUpDown as ArrowUpDownIcon,
  TrendingUp as TrendingUpIcon,
  Check as CheckIcon,
  AlertCircle as AlertCircleIcon,
  Repeat as RepeatIcon,
  Zap as ZapIcon,
  Info as InfoIcon
} from "lucide-vue-next";

// Available cryptocurrencies for exchange
const cryptocurrencies = {
  "btc": "Bitcoin (BTC)",
  "eth": "Ethereum (ETH)",
  "usdt": "Tether (USDT)",
  "usdc": "USD Coin (USDC)",
  "ada": "Cardano (ADA)",
  "sol": "Solana (SOL)",
  "dot": "Polkadot (DOT)",
  "link": "Chainlink (LINK)",
  "matic": "Polygon (MATIC)",
  "avax": "Avalanche (AVAX)"
};

// Mock user balances
const userBalances = {
  "btc": 0.15,
  "eth": 2.5,
  "usdt": 1000,
  "usdc": 500,
  "ada": 2500,
  "sol": 25,
  "dot": 100,
  "link": 200,
  "matic": 5000,
  "avax": 50
};

// Mock exchange rates (everything relative to USD)
const exchangeRates = {
  "btc": 45000,
  "eth": 3200,
  "usdt": 1,
  "usdc": 1,
  "ada": 0.85,
  "sol": 125,
  "dot": 35,
  "link": 28,
  "matic": 1.2,
  "avax": 95
};

// Form state
const fromCurrency = ref("btc");
const toCurrency = ref("eth");
const fromAmount = ref("");
const toAmount = ref("");
const slippageTolerance = ref("0.5");

// UI state
const step = ref(1); // 1: exchange form, 2: review, 3: confirmation
const isLoading = ref(false);
const transactionHash = ref("");
const showAdvanced = ref(false);

// Slippage options
const slippageOptions = {
  "0.1": "0.1%",
  "0.5": "0.5%",
  "1.0": "1.0%",
  "3.0": "3.0%",
  "custom": "Custom"
};

// Computed values
const exchangeRate = computed(() => {
  const fromRate = exchangeRates[fromCurrency.value as keyof typeof exchangeRates] || 1;
  const toRate = exchangeRates[toCurrency.value as keyof typeof exchangeRates] || 1;
  return fromRate / toRate;
});

const userFromBalance = computed(() => {
  return userBalances[fromCurrency.value as keyof typeof userBalances] || 0;
});

const userToBalance = computed(() => {
  return userBalances[toCurrency.value as keyof typeof userBalances] || 0;
});

const exchangeFee = computed(() => {
  const amount = parseFloat(fromAmount.value) || 0;
  const fromRate = exchangeRates[fromCurrency.value as keyof typeof exchangeRates] || 1;
  const usdValue = amount * fromRate;
  return Math.max(usdValue * 0.003, 2); // 0.3% fee, minimum $2
});

const networkFee = computed(() => {
  const fees = {
    "btc": 0.0002,
    "eth": 0.005,
    "usdt": 1,
    "usdc": 1,
    "ada": 0.17,
    "sol": 0.001,
    "dot": 0.01,
    "link": 0.001,
    "matic": 0.01,
    "avax": 0.001
  };
  return fees[toCurrency.value as keyof typeof fees] || 0.001;
});

const priceImpact = computed(() => {
  const amount = parseFloat(fromAmount.value) || 0;
  const fromRate = exchangeRates[fromCurrency.value as keyof typeof exchangeRates] || 1;
  const usdValue = amount * fromRate;
  
  // Simulate price impact based on trade size
  if (usdValue > 100000) return 2.5;
  if (usdValue > 50000) return 1.2;
  if (usdValue > 10000) return 0.5;
  return 0.1;
});

const minimumReceived = computed(() => {
  const baseAmount = parseFloat(toAmount.value) || 0;
  const slippage = parseFloat(slippageTolerance.value) / 100;
  return (baseAmount * (1 - slippage)).toFixed(8);
});

// Helper functions
const getCryptoSymbol = (currency: string) => {
  const symbols = {
    "btc": "BTC",
    "eth": "ETH",
    "usdt": "USDT",
    "usdc": "USDC",
    "ada": "ADA",
    "sol": "SOL",
    "dot": "DOT",
    "link": "LINK",
    "matic": "MATIC",
    "avax": "AVAX"
  };
  return symbols[currency as keyof typeof symbols] || "";
};

const formatBalance = (amount: number, symbol: string) => {
  if (amount >= 1) {
    return `${amount.toFixed(4)} ${symbol}`;
  }
  return `${amount.toFixed(8)} ${symbol}`;
};

// Calculations
const calculateToAmount = (amount: number) => {
  const rate = exchangeRate.value;
  return (amount * rate).toFixed(8);
};

// Watchers for live conversion
watch([fromAmount, fromCurrency, toCurrency], () => {
  if (fromAmount.value) {
    const amount = parseFloat(fromAmount.value);
    if (!isNaN(amount)) {
      toAmount.value = calculateToAmount(amount);
    }
  }
});

// Form handlers
const swapCurrencies = () => {
  const temp = fromCurrency.value;
  fromCurrency.value = toCurrency.value;
  toCurrency.value = temp;
  
  // Clear amounts to recalculate
  fromAmount.value = "";
  toAmount.value = "";
};

const setMaxAmount = () => {
  fromAmount.value = userFromBalance.value.toString();
  toAmount.value = calculateToAmount(userFromBalance.value);
};

const setQuickPercentage = (percentage: number) => {
  const amount = userFromBalance.value * (percentage / 100);
  fromAmount.value = amount.toString();
  toAmount.value = calculateToAmount(amount);
};

const validateForm = () => {
  const amount = parseFloat(fromAmount.value);
  return !isNaN(amount) && 
         amount > 0 && 
         amount <= userFromBalance.value &&
         fromCurrency.value !== toCurrency.value;
};

const proceedToReview = () => {
  if (validateForm()) {
    step.value = 2;
  }
};

const confirmExchange = async () => {
  isLoading.value = true;
  
  // Simulate API call
  setTimeout(() => {
    transactionHash.value = "0x" + Math.random().toString(16).substring(2, 66);
    step.value = 3;
    isLoading.value = false;
  }, 3000);
};

const goBack = () => {
  if (step.value > 1) {
    step.value--;
  }
};

const startNewExchange = () => {
  step.value = 1;
  fromAmount.value = "";
  toAmount.value = "";
  transactionHash.value = "";
};

// Popular exchange pairs
const popularPairs = [
  { from: "BTC", to: "ETH", rate: "14.06" },
  { from: "ETH", to: "USDT", rate: "3,200" },
  { from: "BTC", to: "USDT", rate: "45,000" },
  { from: "ADA", to: "USDC", rate: "0.85" }
];
</script>

<template>
  <BreadCrumbs :title="$t('Exchange Crypto')" class="mb-5" />

  <div class="max-w-2xl mx-auto">
    <!-- Step Indicator -->
    <div class="flex justify-center mb-8">
      <div class="flex items-center space-x-4">
        <div class="flex items-center">
          <div :class="[step >= 1 ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-600', 'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium']">
            1
          </div>
          <span class="ml-2 text-sm">Exchange</span>
        </div>
        <arrow-right-icon class="h-4 w-4 text-gray-400" />
        <div class="flex items-center">
          <div :class="[step >= 2 ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-600', 'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium']">
            2
          </div>
          <span class="ml-2 text-sm">Review</span>
        </div>
        <arrow-right-icon class="h-4 w-4 text-gray-400" />
        <div class="flex items-center">
          <div :class="[step >= 3 ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600', 'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium']">
            <check-icon v-if="step >= 3" class="h-4 w-4" />
            <span v-else>3</span>
          </div>
          <span class="ml-2 text-sm">Confirm</span>
        </div>
      </div>
    </div>

    <!-- Step 1: Exchange Form -->
    <div v-if="step === 1" class="space-y-6">
      <!-- Popular Trading Pairs -->
      <BasicCard>
        <div class="flex items-center gap-3 mb-6">
          <trending-up-icon class="h-6 w-6 text-green-600" />
          <h2 class="text-xl font-semibold">Popular Trading Pairs</h2>
        </div>

        <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div 
            v-for="pair in popularPairs" 
            :key="`${pair.from}-${pair.to}`"
            @click="fromCurrency = pair.from.toLowerCase(); toCurrency = pair.to.toLowerCase()"
            class="p-4 border border-gray-200 rounded-lg cursor-pointer hover:border-gray-300 transition-all"
          >
            <div class="text-center">
              <div class="font-semibold text-sm">{{ pair.from }}/{{ pair.to }}</div>
              <div class="text-xs text-gray-600 mt-1">1 {{ pair.from }} = {{ pair.rate }} {{ pair.to }}</div>
            </div>
          </div>
        </div>
      </BasicCard>

      <!-- Exchange Form -->
      <BasicCard>
        <div class="flex items-center gap-3 mb-6">
          <repeat-icon class="h-6 w-6 text-blue-600" />
          <h2 class="text-xl font-semibold">Exchange Cryptocurrency</h2>
        </div>

        <form @submit.prevent="proceedToReview" class="space-y-6">
          <!-- From Section -->
          <div class="bg-gray-50 p-4 rounded-lg">
            <div class="flex justify-between items-center mb-3">
              <label class="text-sm text-gray-600">From</label>
              <span class="text-xs text-gray-500">
                Balance: {{ formatBalance(userFromBalance, getCryptoSymbol(fromCurrency)) }}
              </span>
            </div>
            
            <div class="flex gap-3">
              <div class="flex-1">
                <TextInput
                  v-model="fromAmount"
                  name="fromAmount"
                  type="number"
                  placeholder="0.00"
                  step="any"
                />
              </div>
              <div class="w-32">
                <InputSelect 
                  v-model="fromCurrency" 
                  :list="cryptocurrencies"
                />
              </div>
            </div>

            <div class="flex justify-between mt-3">
              <div class="flex gap-1">
                <button
                  v-for="percent in [25, 50, 75, 100]"
                  :key="percent"
                  type="button"
                  @click="setQuickPercentage(percent)"
                  class="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                >
                  {{ percent }}%
                </button>
              </div>
              <button
                type="button"
                @click="setMaxAmount"
                class="text-xs text-blue-600 hover:text-blue-700"
              >
                MAX
              </button>
            </div>
          </div>

          <!-- Swap Button -->
          <div class="flex justify-center">
            <button
              type="button"
              @click="swapCurrencies"
              class="p-3 bg-blue-100 text-blue-600 rounded-full hover:bg-blue-200 transition-colors"
            >
              <arrow-up-down-icon class="h-5 w-5" />
            </button>
          </div>

          <!-- To Section -->
          <div class="bg-gray-50 p-4 rounded-lg">
            <div class="flex justify-between items-center mb-3">
              <label class="text-sm text-gray-600">To</label>
              <span class="text-xs text-gray-500">
                Balance: {{ formatBalance(userToBalance, getCryptoSymbol(toCurrency)) }}
              </span>
            </div>
            
            <div class="flex gap-3">
              <div class="flex-1">
                <TextInput
                  v-model="toAmount"
                  name="toAmount"
                  type="text"
                  placeholder="0.00"
                  readonly
                  class="bg-white"
                />
              </div>
              <div class="w-32">
                <InputSelect 
                  v-model="toCurrency" 
                  :list="cryptocurrencies"
                />
              </div>
            </div>
          </div>

          <!-- Exchange Rate Info -->
          <div v-if="fromAmount && toAmount" class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div class="flex justify-between items-center text-sm">
              <span>Exchange Rate</span>
              <span class="font-medium">
                1 {{ getCryptoSymbol(fromCurrency) }} = {{ exchangeRate.toFixed(8) }} {{ getCryptoSymbol(toCurrency) }}
              </span>
            </div>
            <div class="flex justify-between items-center text-sm mt-2" v-if="priceImpact > 0.5">
              <span class="text-orange-600">Price Impact</span>
              <span class="font-medium text-orange-600">{{ priceImpact.toFixed(2) }}%</span>
            </div>
          </div>

          <!-- Advanced Settings -->
          <div>
            <button
              type="button"
              @click="showAdvanced = !showAdvanced"
              class="text-sm text-blue-600 hover:text-blue-700 mb-3"
            >
              {{ showAdvanced ? 'Hide' : 'Show' }} Advanced Settings
            </button>
            
            <div v-if="showAdvanced" class="space-y-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <label class="text-sm text-gray-600 mb-2 block">Slippage Tolerance</label>
                <InputSelect 
                  v-model="slippageTolerance" 
                  :list="slippageOptions"
                  placeholder="Select slippage"
                />
                <p class="text-xs text-gray-500 mt-1">
                  Your transaction will revert if the price changes unfavorably by more than this percentage.
                </p>
              </div>
            </div>
          </div>

          <!-- Validation Messages -->
          <div v-if="fromAmount && parseFloat(fromAmount) > userFromBalance" class="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <alert-circle-icon class="h-5 w-5 text-red-600" />
            <span class="text-sm text-red-800">Insufficient {{ getCryptoSymbol(fromCurrency) }} balance</span>
          </div>

          <div v-if="fromCurrency === toCurrency" class="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <alert-circle-icon class="h-5 w-5 text-yellow-600" />
            <span class="text-sm text-yellow-800">Please select different currencies to exchange</span>
          </div>

          <!-- Action Button -->
          <div class="flex justify-end pt-4">
            <button
              type="submit"
              :disabled="!validateForm()"
              class="px-8 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              Review Exchange
            </button>
          </div>
        </form>
      </BasicCard>
    </div>

    <!-- Step 2: Review Exchange -->
    <BasicCard v-if="step === 2">
      <div class="space-y-6">
        <div class="flex items-center gap-3 mb-6">
          <info-icon class="h-6 w-6 text-orange-600" />
          <h2 class="text-xl font-semibold">Review Exchange</h2>
        </div>

        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div class="flex items-start gap-3">
            <alert-circle-icon class="h-5 w-5 text-yellow-600 mt-0.5" />
            <div>
              <h3 class="font-medium text-yellow-800 mb-1">Review Carefully</h3>
              <p class="text-sm text-yellow-700">
                Please verify the exchange details. This action cannot be undone once confirmed.
              </p>
            </div>
          </div>
        </div>

        <!-- Exchange Summary -->
        <div class="space-y-4">
          <div class="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p class="text-sm text-gray-600">You're exchanging</p>
              <p class="font-semibold text-lg">{{ fromAmount }} {{ getCryptoSymbol(fromCurrency) }}</p>
            </div>
            <arrow-right-icon class="h-6 w-6 text-gray-400" />
            <div class="text-right">
              <p class="text-sm text-gray-600">You'll receive</p>
              <p class="font-semibold text-lg">{{ toAmount }} {{ getCryptoSymbol(toCurrency) }}</p>
            </div>
          </div>

          <!-- Rate and Details -->
          <div class="grid grid-cols-2 gap-4">
            <div class="p-4 bg-gray-50 rounded-lg">
              <p class="text-sm text-gray-600 mb-1">Exchange Rate</p>
              <p class="font-medium">1 {{ getCryptoSymbol(fromCurrency) }} = {{ exchangeRate.toFixed(8) }} {{ getCryptoSymbol(toCurrency) }}</p>
            </div>
            <div class="p-4 bg-gray-50 rounded-lg">
              <p class="text-sm text-gray-600 mb-1">Price Impact</p>
              <p :class="[
                'font-medium',
                priceImpact > 1 ? 'text-red-600' : priceImpact > 0.5 ? 'text-orange-600' : 'text-green-600'
              ]">{{ priceImpact.toFixed(2) }}%</p>
            </div>
          </div>

          <!-- Fee Breakdown -->
          <div class="bg-gray-50 p-4 rounded-lg space-y-2">
            <h3 class="font-medium text-gray-800 mb-3">Fee Breakdown</h3>
            <div class="flex justify-between text-sm">
              <span>Exchange Fee (0.3%)</span>
              <span>${{ exchangeFee.toFixed(2) }}</span>
            </div>
            <div class="flex justify-between text-sm">
              <span>Network Fee</span>
              <span>{{ networkFee }} {{ getCryptoSymbol(toCurrency) }}</span>
            </div>
            <div class="flex justify-between text-sm">
              <span>Slippage Tolerance</span>
              <span>{{ slippageTolerance }}%</span>
            </div>
            <div class="border-t pt-2 flex justify-between font-medium">
              <span>Minimum Received</span>
              <span>{{ minimumReceived }} {{ getCryptoSymbol(toCurrency) }}</span>
            </div>
          </div>

          <!-- Security Notice -->
          <div class="bg-green-50 border border-green-200 rounded-lg p-4">
            <div class="flex items-start gap-3">
              <zap-icon class="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <h3 class="font-medium text-green-800 mb-1">Instant Exchange</h3>
                <p class="text-sm text-green-700">
                  Your exchange will be processed immediately and the new cryptocurrency will appear in your wallet within minutes.
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-between pt-6">
          <button
            @click="goBack"
            class="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Back
          </button>
          <button
            @click="confirmExchange"
            :disabled="isLoading"
            class="px-8 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <span v-if="isLoading">Processing Exchange...</span>
            <span v-else>Confirm Exchange</span>
          </button>
        </div>
      </div>
    </BasicCard>

    <!-- Step 3: Confirmation -->
    <BasicCard v-if="step === 3">
      <div class="text-center space-y-6">
        <div class="flex justify-center">
          <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <check-icon class="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div>
          <h2 class="text-2xl font-semibold text-green-800 mb-2">Exchange Successful!</h2>
          <p class="text-gray-600">
            Your cryptocurrency exchange has been completed successfully.
          </p>
        </div>

        <div class="bg-gray-50 p-4 rounded-lg">
          <p class="text-sm text-gray-600 mb-2">Transaction Hash</p>
          <code class="text-sm font-mono bg-white p-2 rounded border block break-all">
            {{ transactionHash }}
          </code>
        </div>

        <div class="space-y-2">
          <p class="font-medium">Exchanged {{ fromAmount }} {{ getCryptoSymbol(fromCurrency) }}</p>
          <p class="font-medium">Received {{ toAmount }} {{ getCryptoSymbol(toCurrency) }}</p>
          <p class="text-sm text-gray-600">Exchange rate: 1 {{ getCryptoSymbol(fromCurrency) }} = {{ exchangeRate.toFixed(8) }} {{ getCryptoSymbol(toCurrency) }}</p>
        </div>

        <div class="flex justify-center gap-4 pt-6">
          <button
            @click="startNewExchange"
            class="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Exchange More
          </button>
          <button
            @click="$router.push({ name: 'account.crypto' })"
            class="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            View Portfolio
          </button>
        </div>
      </div>
    </BasicCard>
  </div>
</template>

<style scoped lang="scss">

</style>
